/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, February 16th 2022, 12:39:10 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { Edit as EditIcon } from "react-feather";
import IconButton from "@mui/material/IconButton";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { COLORSCHEME } from "theme/styles";

type ToggleEditFunction = () => void;

type RenderEditComponentSpec = (args: {
  toggleEditMode: ToggleEditFunction;
  setValueInputRef: (targetElement: HTMLInputElement) => void;
}) => React.ReactNode;

type RenderChildrenSpec = (args: { toggleEditMode: ToggleEditFunction; editIcon: React.ReactNode }) => React.ReactNode;

interface EditTogglerProps {
  renderEditComponent: RenderEditComponentSpec;
  children: RenderChildrenSpec;
  initialEditMode?: boolean;
  editModeCondition?: boolean;
  hasError?: boolean;
}

export const EditToggler = ({
  children,
  renderEditComponent,
  initialEditMode = false,
  editModeCondition = true,
  hasError = false,
}: EditTogglerProps) => {
  const [isEditMode, setIsEditMode] = React.useState(initialEditMode);

  const toggleEditMode = () => setIsEditMode((previous) => !previous && editModeCondition);

  const setValueInputRef = React.useCallback(
    (targetInputElement: HTMLInputElement) => {
      !hasError && targetInputElement?.focus?.();
    },
    [hasError]
  );

  const handleEditComponentClickAway = (event) => {
    if (event.target.localName === "body") return; // For MUI select elements, options are portaled to the end of the body (https://github.com/mui/material-ui/issues/12034#issuecomment-401908298), so we ignore a clickaway event here.

    toggleEditMode();
  };

  const editIcon = editModeCondition ? <EditButton toggleEditMode={toggleEditMode} /> : null;

  /** Using a div for Clickaway child because ClickAwayListener child needs to hold a ref https://mui.com/base/api/click-away-listener/#props */

  return isEditMode || hasError ? (
    <ClickAwayListener onClickAway={handleEditComponentClickAway}>
      <div style={{ width: "100%" }}>{renderEditComponent({ toggleEditMode, setValueInputRef })}</div>
    </ClickAwayListener>
  ) : (
    <>{children({ toggleEditMode, editIcon })}</>
  );
};

const EditButton = ({ toggleEditMode }) => (
  <IconButton onClick={toggleEditMode}>
    <EditIcon size={16} color={COLORSCHEME.primary} />
  </IconButton>
);
